import { jsxs, jsx } from 'react/jsx-runtime';
import { useCallback, Fragment } from 'react';
import styled from 'styled-components';
import { groupBy } from '../../helpers/groupBy.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import '../../hooks/useTree/useTreeContent.js';
import { useTreeController } from '../../hooks/useTree/useTreeController.js';
import { Heading } from '../Heading/Heading.js';
import { ListItem } from '../List/ListItem.js';
import { Loader } from '../Loader/Loader.js';
import { SidebarNavigationButton } from './SidebarNavigationButton.js';
import { SidebarNavigationLink } from './SidebarNavigationLink.js';
import { SidebarSubNavigation } from './SidebarSubNavigation.js';

const SidebarNavigationTreeHeading = styled(Heading).withConfig({
  componentId: "sc-8eyty5-0"
})(["padding:", ";"], ({
  theme
}) => theme.components.sidebar.headingPadding);
const SidebarNavigationTree = /*#__PURE__*/withTitan(({
  item,
  selectedDepth,
  matchFunction,
  onClickButton,
  onClickLink,
  onHideDrawer,
  onCloseSubMenu
}) => {
  const {
    children,
    isActiveTrail,
    title,
    depth
  } = item;
  const groupedItems = groupBy(children.items, 'section', title);
  return jsx(SidebarSubNavigation, {
    depth: depth,
    isShown: selectedDepth - 1 >= depth && isActiveTrail,
    title: title,
    onCloseSubMenu: onCloseSubMenu,
    children: children.isLoading ? jsx(Loader, {
      height: 68
    }) : groupedItems.map(([section, items], i) =>
    // eslint-disable-next-line react/no-array-index-key
    jsxs(Fragment, {
      children: [jsx(SidebarNavigationTreeHeading, {
        heading: "h3",
        children: section
      }), items.map(child => jsx(SidebarNavigationItem, {
        item: child,
        matchFunction: matchFunction,
        selectedDepth: selectedDepth,
        onClickButton: onClickButton,
        onClickLink: onClickLink,
        onCloseSubMenu: onCloseSubMenu,
        onHideDrawer: onHideDrawer
      }, `sidebar-child-${child.depth}-${child.id}`))]
    }, `sidebar-child-${section}-${i}`))
  });
}, {
  name: 'SidebarNavigationTree'
});
const SidebarNavigationItem = /*#__PURE__*/withTitan(({
  item,
  selectedDepth,
  isSelecting,
  matchFunction,
  onClickButton,
  onClickLink,
  onHideDrawer,
  onCloseSubMenu,
  testId = 'sidebarNavigationItem'
}) => {
  const {
    setActiveId
  } = useTreeController();
  const handleClick = useCallback(() => {
    if (!item.isActiveTrail) {
      setActiveId(item.id);
    }
    onClickButton(item);
  }, [onClickButton, item, setActiveId]);
  const handleClickLink = useCallback(() => {
    setActiveId(item.id);
    onClickLink();
  }, [onClickLink, item, setActiveId]);
  // If item has children this will return a recursive SidebarNavigationItem with a SidebarNavigationButton
  // Read more about recursion in React:
  // @see https://dev.to/jsmanifest/create-a-modern-dynamic-sidebar-menu-in-react-using-recursion-36eo
  if (item.hasChildren) {
    return jsxs(ListItem, {
      testId: `${testId}WithChildren`,
      children: [jsx(SidebarNavigationButton, {
        item: item,
        onClick: handleClick
      }), jsx(SidebarNavigationTree, {
        item: item,
        matchFunction: matchFunction,
        selectedDepth: selectedDepth,
        onClickButton: onClickButton,
        onClickLink: onClickLink,
        onCloseSubMenu: onCloseSubMenu,
        onHideDrawer: onHideDrawer
      })]
    });
  }
  // If item is a regular link will return a SidebarNavigationLink
  return jsx(ListItem, {
    testId: `${testId}Link`,
    children: jsx(SidebarNavigationLink, {
      isSelecting: isSelecting,
      item: item,
      matchFunction: matchFunction,
      onClickLink: handleClickLink
    })
  });
}, {
  name: 'SidebarNavigationItem'
});

export { SidebarNavigationItem };
//# sourceMappingURL=SidebarNavigationItem.js.map
